import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Hls from 'hls.js';
import { useShop } from '@/providers/ShopProvider';
import { RiVolumeMuteFill, RiVolumeUpFill } from '@remixicon/react';

interface QueueVideoProps {
  video: { url: string; duration: number };
  targetTimestamp: number;
  onVideoEnd: () => void;
}

function padNumber(num: number): string {
  return num.toString().padStart(2, '0');
}

function formatCountdown(totalSeconds: number) {
  const clamped = Math.max(0, totalSeconds);
  const minutes = Math.floor(clamped / 60);
  const seconds = Math.floor(clamped % 60);
  return { minutes: padNumber(minutes), seconds: padNumber(seconds) };
}

export function QueueVideo({ video, targetTimestamp, onVideoEnd }: QueueVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [muted, setMuted] = useState(true);
  const [visible, setVisible] = useState(true);
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    Math.max(0, targetTimestamp - Math.floor(Date.now() / 1000))
  );
  const { isWidget } = useShop();

  const videoUrl = video.url;
  const videoDuration = video.duration;

  const setupVideo = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const seekToSync = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = targetTimestamp - now;
      const seekTime = videoDuration - remaining;
      if (seekTime > 0 && seekTime < videoDuration) {
        videoEl.currentTime = seekTime;
      }
      videoEl.play().catch(() => {
        // Autoplay blocked - video stays paused, user can interact
      });
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        seekToSync();
      });
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari)
      videoEl.src = videoUrl;
      videoEl.addEventListener('loadedmetadata', () => {
        seekToSync();
      }, { once: true });
    }
  }, [videoUrl, videoDuration, targetTimestamp]);

  useEffect(() => {
    setupVideo();
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [setupVideo]);

  // Countdown tick
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = targetTimestamp - now;
      setSecondsRemaining(Math.max(0, remaining));

      if (remaining <= 0) {
        setVisible(false);
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetTimestamp]);

  const handleExitComplete = () => {
    hlsRef.current?.destroy();
    hlsRef.current = null;
    onVideoEnd();
  };

  const toggleMute = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.muted = !videoEl.muted;
    setMuted(!muted);
  };

  const countdown = formatCountdown(secondsRemaining);

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className={
            isWidget
              ? 'absolute inset-0 z-50 bg-black flex items-center justify-center'
              : 'fixed inset-0 z-50 bg-black flex items-center justify-center'
          }
        >
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            muted
            playsInline
            autoPlay
          />

          {/* Countdown overlay */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center pointer-events-none">
            <div className="flex gap-2 text-white font-mono text-5xl bg-black/60 rounded-xl px-6 py-4 backdrop-blur-sm">
              <span>{countdown.minutes}</span>
              <span className="animate-pulse">:</span>
              <span>{countdown.seconds}</span>
            </div>
          </div>

          {/* Unmute button */}
          <button
            onClick={toggleMute}
            className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 backdrop-blur-sm transition-colors"
          >
            {muted ? (
              <RiVolumeMuteFill className="h-6 w-6" />
            ) : (
              <RiVolumeUpFill className="h-6 w-6" />
            )}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
